import { ReactNode } from 'react'
import cn from 'classnames'

/* Small pieces the v2 editor shares with the setup wizard, kept in the module. */

export const LinkButton = ({
   children,
   onClick,
   disabled,
   className,
   testId,
}: {
   children: ReactNode
   onClick: () => void
   disabled?: boolean
   className?: string
   testId?: string
}) => (
   <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
         'text-[#A684FF] text-sm font-medium hover:underline shrink-0 disabled:opacity-40 disabled:no-underline',
         className
      )}
   >
      {children}
   </button>
)

export const Chip = ({
   children,
   onRemove,
   onAdd,
   selected,
   disabled,
   title,
   testId,
   kind,
}: {
   children: ReactNode
   onRemove?: () => void
   onAdd?: () => void
   selected?: boolean
   disabled?: boolean
   title?: string
   testId?: string
   /** Small type label, e.g. "stage" or "tag". */
   kind?: string
}) => {
   const base =
      'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[13px] leading-5 transition-colors'
   if (onAdd) {
      return (
         <button
            type="button"
            title={title}
            disabled={disabled}
            onClick={onAdd}
            data-testid={testId}
            className={cn(
               base,
               'border-[#9F7BFF]/40 bg-[#9F7BFF]/10 text-white hover:bg-[#9F7BFF]/25',
               disabled && 'opacity-40 cursor-not-allowed'
            )}
         >
            <span className="text-[#C9B8FF]">+</span>
            {children}
            {kind && (
               <span className="text-white/40 text-[10px] uppercase tracking-wide">
                  {kind}
               </span>
            )}
         </button>
      )
   }
   return (
      <span
         title={title}
         data-testid={testId}
         className={cn(
            base,
            selected === false
               ? 'border-white/10 bg-white/[0.03] text-white/60'
               : 'border-white/15 bg-white/[0.08] text-white'
         )}
      >
         {children}
         {kind && (
            <span className="text-white/40 text-[10px] uppercase tracking-wide">
               {kind}
            </span>
         )}
         {onRemove && (
            <button
               type="button"
               aria-label={`Remove ${typeof children === 'string' ? children : ''}`}
               className="ml-0.5 w-4 h-4 rounded-full bg-white/10 hover:bg-white/30 text-white/70 hover:text-white text-[11px] leading-none flex items-center justify-center"
               onClick={onRemove}
            >
               ×
            </button>
         )}
      </span>
   )
}

export interface IGlimpseRow {
   id: string
   icon: ReactNode
   label: string
   value: ReactNode
   sub?: ReactNode
   /** Missing or still being asked for. */
   pending?: boolean
   /** Lit while its editor is open on the right. */
   active?: boolean
   onEdit?: () => void
   editLabel?: string
}

/** The left-hand column of the editor: every detail at a glance, each with its own Edit. */
export const CampaignGlimpse = ({
   title,
   rows,
}: {
   title: string
   rows: IGlimpseRow[]
}) => (
   <aside
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 md:p-4"
      data-testid="campaign-glimpse"
   >
      <p className="text-white font-semibold text-sm px-1 pb-3">{title}</p>
      <ul className="space-y-1.5">
         {rows.map((row) => (
            <li
               key={row.id}
               data-testid={`glimpse-${row.id}`}
               data-state={row.pending ? 'pending' : 'set'}
               className={cn(
                  'flex items-start gap-3 rounded-xl px-3 py-2.5 border transition-colors',
                  row.active
                     ? 'border-[#9F7BFF]/40 bg-[#9F7BFF]/10'
                     : 'border-transparent'
               )}
            >
               <span
                  className={cn(
                     'flex shrink-0 items-center justify-center w-7 h-7 rounded-lg',
                     row.pending
                        ? 'bg-white/[0.04] text-white/35'
                        : 'bg-[#9F7BFF]/15 text-[#A684FF]'
                  )}
               >
                  {row.icon}
               </span>
               <span className="flex-1 min-w-0">
                  <span className="flex items-center justify-between gap-2">
                     <span className="text-[11px] uppercase tracking-wide text-white/40">
                        {row.label}
                     </span>
                     {row.onEdit && (
                        <LinkButton
                           onClick={row.onEdit}
                           className="!text-xs"
                           testId={`edit-${row.id}`}
                        >
                           {row.editLabel || 'Edit'}
                        </LinkButton>
                     )}
                  </span>
                  <span
                     className={cn(
                        'block text-[13px] leading-5 mt-0.5',
                        row.pending ? 'text-white/45 italic' : 'text-white'
                     )}
                  >
                     {row.value}
                  </span>
                  {row.sub && (
                     <span className="block text-xs text-white/45 mt-0.5">
                        {row.sub}
                     </span>
                  )}
               </span>
            </li>
         ))}
      </ul>
   </aside>
)
